import { useState } from "react";
import { Copy, Download, Check, Landmark } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { API, api } from "../../lib/api";
import { reportError } from "../../lib/reportError";

export const BankPaymentDetails = ({ payment }) => {
  const { t, language } = useLanguage();
  const [copied, setCopied] = useState("");
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { creditor } = payment;
  const address = creditor.address;
  const basePath = `/orders/${payment.order_id}/bank-payment`;
  const copy = async (key, value) => {
    setError("");
    try { await navigator.clipboard.writeText(value); setCopied(key); }
    catch (error) { reportError("Bank detail copy failed", error); setError("Could not copy. Please copy the displayed value manually."); }
  };
  const download = async () => {
    setDownloading(true); setError("");
    try {
      const { data } = await api.get(`${basePath}/bill.pdf`, { params: { language }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url; link.download = `Evi-Studio-QR-${payment.order_id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { reportError("QR PDF download failed", error); setError("The PDF could not be downloaded. Please try again."); }
    finally { setDownloading(false); }
  };
  const fields = [
    { key: "iban", label: "IBAN", value: creditor.account, display: creditor.account.match(/.{1,4}/g).join(" ") },
    { key: "reference", label: "Payment reference", value: payment.reference, display: payment.reference.match(/.{1,4}/g).join(" ") },
  ];
  return <section data-testid="bank-payment-details" className="mt-8 border-y border-[#282C3D] py-8">
    <div className="grid items-start gap-8 sm:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
      <div>
        <div className="mx-auto aspect-square w-full max-w-[240px] overflow-hidden rounded-md bg-white">
          <img data-testid="bank-payment-qr" src={`${API}${basePath}/qr.svg`} alt={t("Swiss payment QR code")} className="h-full w-full object-contain" onError={() => setImageError(true)} />
        </div>
        {imageError && <p data-testid="bank-qr-error" role="alert" className="mt-3 text-sm text-rose-300">{t("The QR preview could not be loaded. You can still download the PDF.")}</p>}
        <button data-testid="bank-pdf-download" onClick={download} disabled={downloading} className="btn-ghost mt-4 w-full"><Download size={16} />{t(downloading ? "Downloading…" : "Download QR payment slip")}</button>
      </div>
      <div className="min-w-0 space-y-6">
        <div><p className="eyebrow">{t("Amount")}</p><p data-testid="bank-payment-amount" className="mt-2 font-display text-3xl font-bold text-[#FFC857]">CHF {payment.amount}</p></div>
        <div><p className="eyebrow">{t("Payable to")}</p><p data-testid="bank-creditor" className="mt-2 text-sm leading-relaxed text-slate-200">{address.name}<br />{address.street} {address.house_num}<br />{address.pcode} {address.city}<br />{t("Switzerland")}</p></div>
        {fields.map(field => <div key={field.key}>
          <p className="eyebrow">{t(field.label)}</p><div className="mt-2 flex items-start gap-3">
            <p data-testid={`bank-${field.key}-value`} className="min-w-0 flex-1 break-words font-mono text-sm text-slate-200">{field.display}</p>
            <button type="button" data-testid={`bank-copy-${field.key}`} onClick={() => copy(field.key, field.value)} aria-label={t("Copy {{field}}", { field: t(field.label) })} title={t("Copy {{field}}", { field: t(field.label) })} className="shrink-0 p-1 text-[#E5A93C] transition-colors hover:text-[#FFC857]">{copied === field.key ? <Check size={16} /> : <Copy size={16} />}</button>
          </div>
        </div>)}
        <p data-testid="bank-account-details" className="flex items-center gap-2 text-xs text-slate-500"><Landmark size={16} className="shrink-0" />{creditor.bic} · {creditor.postal_account}</p>
      </div>
    </div>
    {copied && <p data-testid="bank-copy-status" role="status" className="mt-4 text-sm text-emerald-400">{t("Copied")}</p>}
    {error && <p data-testid="bank-document-error" role="alert" className="mt-4 text-sm text-rose-300">{t(error)}</p>}
  </section>;
};